import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    BarChart3,
    Bomb,
    BookOpen,
    CalendarDays,
    Check,
    ChevronRight,
    Crosshair,
    Database,
    Map,
    MessageSquare,
    Play,
    Radar,
    Sparkles,
    Swords,
    Trophy,
    Users,
    Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useHeroVideo } from '../hooks/useHeroVideo';
import { PREMIUM_TEAM_SCOUT_MAX_DEMOS_PER_ANALYSIS } from '../config/scoutCapacity';
import './LoginPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

type LandingFeature = {
    eyebrow: string;
    icon: LucideIcon;
    title: string;
    description: string;
    highlights: string[];
    accent: 'blue' | 'violet' | 'cyan' | 'orange';
    videoSrc?: string;
    videoPoster?: string;
    detail?: { title: string; description: string };
};

const WORKFLOW_PHASES: Array<{
    number: string;
    kicker: string;
    title: string;
    description: string;
    steps: Array<{
        number: string;
        icon: LucideIcon;
        title: string;
        description: string;
    }>;
}> = [
    {
        number: '01',
        kicker: 'Organize',
        title: 'Build your season foundation.',
        description: 'Bring the competitive schedule and the whole roster into one shared system.',
        steps: [
            {
                number: '01',
                icon: Trophy,
                title: 'Import your league',
                description: 'Track your favorite team and import an existing FACEIT or ESEA league into StratSync in a few clicks.',
            },
            {
                number: '02',
                icon: BarChart3,
                title: 'Follow the season',
                description: 'Keep fixtures, results, standings and upcoming opponents visible from one team dashboard.',
            },
            {
                number: '03',
                icon: CalendarDays,
                title: 'Manage availability',
                description: 'Schedule matches and practice sessions while always seeing when the full roster is available.',
            },
        ],
    },
    {
        number: '02',
        kicker: 'Prepare',
        title: 'Turn information into a plan.',
        description: 'Scout what is coming, learn from the best and turn the findings into executable strategies.',
        steps: [
            {
                number: '04',
                icon: Radar,
                title: 'Analyze your opponent',
                description: 'Stack up to 20 opponent demos in one Deep Dive to expose setups, rotations, utility and player tendencies.',
            },
            {
                number: '05',
                icon: Play,
                title: 'Learn from pro demos',
                description: 'Study professional matches and export a selected round into your stratbook with one click.',
            },
            {
                number: '06',
                icon: BookOpen,
                title: 'Create strategies',
                description: 'Build strategies and counter-strats, record them on a Dry Run Server and import the session into your stratbook.',
            },
        ],
    },
    {
        number: '03',
        kicker: 'Practice',
        title: 'Rehearse until it is repeatable.',
        description: 'Keep utility current, find the right opponents and move every new idea onto a live server.',
        steps: [
            {
                number: '07',
                icon: Bomb,
                title: 'Maintain your lineup archive',
                description: 'Record team lineups, keep them organized by map and automatically render reusable videos.',
            },
            {
                number: '08',
                icon: Zap,
                title: 'Run utility sessions',
                description: 'Import lineups on a Lineup Server or practice your own archive in a purpose-built Utility Run challenge.',
            },
            {
                number: '09',
                icon: Swords,
                title: 'Find scrims and host practice',
                description: 'Find opponents for the next match and start a dedicated game server to rehearse new strategies together.',
            },
        ],
    },
    {
        number: '04',
        kicker: 'Improve',
        title: 'Learn from every match.',
        description: 'Warm up before the server goes live, review the result together and turn strong performances into rewards.',
        steps: [
            {
                number: '10',
                icon: Crosshair,
                title: 'Stay warmed up',
                description: 'Join the public Deathmatch server or start focused Pre-Fire, Cross-Fire, Peek-Fire, Swarmfire, Defend and Spray Training sessions.',
            },
            {
                number: '11',
                icon: Database,
                title: 'Review every match',
                description: 'After the match, demos transfer automatically to StratSync for 2D replay and synchronized Watch Together reviews.',
            },
            {
                number: '12',
                icon: Sparkles,
                title: 'Turn plays into rewards',
                description: 'Create highlight clips, earn raffle points through Deathmatch and aim-mode performance, or win the weekly highlight vote for prizes.',
            },
        ],
    },
];

const LANDING_FEATURES: LandingFeature[] = [
    {
        eyebrow: 'Practice game modes',
        icon: Crosshair,
        title: 'Train the situations that decide rounds.',
        description: 'Move beyond generic warmups. Launch focused CS2 sessions that sharpen entries, trades, peeks, survival, site holds and recoil control with repeatable, map-specific practice.',
        highlights: ['Pre-Fire', 'Cross-Fire', 'Peek-Fire', 'Swarmfire', 'Defend', 'Spray Training'],
        accent: 'blue',
        videoSrc: '/videos/landing/practice-game-modes.mp4',
        videoPoster: '/videos/landing/practice-game-modes.jpg',
    },
    {
        eyebrow: 'Utility & servers',
        icon: Bomb,
        title: 'Take utility from idea to execution.',
        description: 'Keep a shared lineup archive, record throws and render reusable videos. Import lineups on a dedicated Lineup Server or start a Utility Run Server to practice your own archive as a purpose-built challenge.',
        highlights: ['Lineup Archive', 'Video Rendering', 'Lineup Import Server', 'Utility Run Server'],
        accent: 'cyan',
        videoSrc: '/videos/landing/utility-servers.mp4',
        videoPoster: '/videos/landing/utility-servers.jpg',
    },
    {
        eyebrow: 'Team stratbook',
        icon: BookOpen,
        title: 'Give every call a shared visual language.',
        description: 'Discover pro plays with the Strategy Finder and build your own calls with the Strategy Creator. Create team strategies and counter-strats on interactive CS2 radars, or record a session on a Dry Run Server and import it into your stratbook with one click.',
        highlights: ['Strategy Finder', 'Strategy Creator', 'Team Strategies', 'Counter-Strats', 'Dry Run Server', 'One-Click Dry Run Import'],
        accent: 'violet',
        videoSrc: '/videos/landing/team-stratbook.mp4',
        videoPoster: '/videos/landing/team-stratbook.jpg',
    },
    {
        eyebrow: 'Opponent analysis',
        icon: Radar,
        title: 'Turn opponent habits into a match plan.',
        description: `Stack up to ${PREMIUM_TEAM_SCOUT_MAX_DEMOS_PER_ANALYSIS} demos into one Deep Dive picture of your opponent. Expose recurring setups, rotations, utility patterns and player tendencies, then jump into the exact rounds and turn the findings into counter-strats.`,
        highlights: ['Deep Dive Analysis', '2D Demo Viewer', 'Watch Together', 'Counter-Strats'],
        accent: 'orange',
        videoSrc: '/videos/landing/opponent-analysis.mp4',
        videoPoster: '/videos/landing/opponent-analysis.jpg',
    },
    {
        eyebrow: 'Scrims & match servers',
        icon: Swords,
        title: 'Find your next scrim. Make every round count.',
        description: 'Start with Scrim Search: find opponents, agree on maps and organize your next practice match. Then bring both teams onto your Scrim Server, with a match config and match plugin built for coordinated team sessions.',
        highlights: ['Scrim Search', 'Scrim Server', 'Match Config', 'Match Plugin'],
        detail: {
            title: 'Round Echo',
            description: 'Create your Echo Library from pro demos or your recent games. Replay selected rounds against the recorded opposition on your practice server, choose from different difficulties and rehearse the situations your team wants to master.',
        },
        accent: 'cyan',

    },
    {
        eyebrow: 'Demo Vault',
        icon: Database,
        title: 'Your matches, ready for the next review.',
        description: 'Keep your team’s match recordings in one Demo Vault. Automatically receive demos from your linked FACEIT matches and scrims played on your StratSync Scrim Server. Turn a stored demo into a 2D replay with one click, then revisit key rounds and review the decisions together.',
        highlights: ['Automatic FACEIT Import', 'Automatic Scrim Demos', 'One-Click 2D Replay', 'Team Demo Vault'],
        accent: 'blue',

    },
];

const SUPPORTING_FEATURES: Array<{
    icon: LucideIcon;
    title: string;
    description: string;
}> = [
    { icon: CalendarDays, title: 'Team Calendar', description: 'Availability, matches and practice in one shared schedule.' },
    { icon: Users, title: 'Roster Management', description: 'Roles, permissions and the active roster at a glance.' },
    { icon: Trophy, title: 'League Management', description: 'Keep fixtures, standings and upcoming opponents connected to your season.' },
    { icon: Map, title: 'Practice Roadmap', description: 'Prioritize maps and preparation around upcoming matches.' },
    { icon: MessageSquare, title: 'Team Communication', description: 'Coordinate through StratSync and the Telegram bridge.' },
    { icon: Sparkles, title: 'Highlights & Rewards', description: 'Turn memorable plays into clips and take part in community challenges and raffles.' },
];

const PRICING_PLANS = [
    {
        name: 'Player Premium',
        audience: 'For individual improvement',
        price: '5.99',
        features: ['Personal practice servers', 'Unlimited unranked practice runs', 'Lineup import & utility run', 'Expanded personal demo storage'],
    },
    {
        name: 'Team Premium',
        audience: 'For competitive rosters',
        price: '29.99',
        featured: true,
        delivery: 'On Demand · Dynamic IP',
        deliveryNote: '+ €5/month for a Dedicated Server · Optional',
        features: ['Team stratbook & lineup workflows', 'Opponent Deep Dive Analysis', 'Up to 85 team demos', 'Unlimited 2D replays'],
    },
    {
        name: 'Team + 5 Players',
        audience: 'The complete team setup',
        price: '54.99',
        delivery: 'Dedicated · Fixed IP',
        deliveryNote: 'Provisioning within 24 hours. Use on-demand servers while you wait.',
        features: ['Everything in Team Premium', 'Player Premium for five starters', 'One coordinated team package', 'Built for a full main roster'],
    },
];

function FeatureVideo({ src, poster, label }: { src: string; poster: string; label: string }) {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let isVisible = false;

        const syncPlayback = () => {
            if (isVisible && !reducedMotion.matches) {
                void video.play().catch(() => undefined);
            } else {
                video.pause();
            }
        };

        const observer = new IntersectionObserver(
            ([entry]) => {
                isVisible = entry.isIntersecting;
                syncPlayback();
            },
            { threshold: 0.2, rootMargin: '120px 0px' },
        );

        observer.observe(video);
        reducedMotion.addEventListener('change', syncPlayback);

        return () => {
            observer.disconnect();
            reducedMotion.removeEventListener('change', syncPlayback);
            video.pause();
        };
    }, []);

    return (
        <div className="feature-media">
            <video
                ref={videoRef}
                className="feature-media__video"
                src={src}
                poster={poster}
                loop
                muted
                playsInline
                preload="metadata"
                disablePictureInPicture
                aria-label={label}
            />
        </div>
    );
}

function HeroRankJourney() {
    return (
        <div className="hero-rank-journey landing-reveal is-visible">
            <div className="hero-rank-journey__visual">
                <img
                    src="/images/landing-team-rank-journey-vertical-v2.png"
                    alt="Four competitive league tiers rising from Open through Intermediate and Main to Advanced"
                    width={1086}
                    height={1448}
                    decoding="async"
                    fetchPriority="high"
                />
            </div>
            <div className="hero-rank-journey__content">
                <span className="hero-rank-journey__eyebrow"><Trophy size={14} /> Built to progress</span>
                <h2>Climb every tier. Together.</h2>
                <p>Build the habits, strategy and team chemistry that take your roster from Open to Advanced.</p>
            </div>
        </div>
    );
}

export function LoginPage() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { videoRef: heroVideoRef } = useHeroVideo();

    useEffect(() => {
        if (isAuthenticated) navigate('/team-selection', { replace: true });
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        document.documentElement.style.overflow = 'auto';
        document.documentElement.style.scrollBehavior = 'smooth';
        document.body.style.overflow = 'auto';

        return () => {
            document.documentElement.style.overflow = '';
            document.documentElement.style.scrollBehavior = '';
            document.body.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => entries.forEach(entry => {
                if (entry.isIntersecting) entry.target.classList.add('is-visible');
            }),
            { threshold: 0.12, rootMargin: '0px 0px -70px' },
        );

        const elements = document.querySelectorAll('.landing-reveal');
        elements.forEach(element => observer.observe(element));
        return () => observer.disconnect();
    }, []);

    const handleFaceitLogin = () => {
        window.location.href = `${API}/auth/faceit`;
    };

    return (
        <div className="login-page-container">
            <div className="landing-background" aria-hidden="true">
                <div className="landing-background__grid" />
                <div className="landing-background__glow landing-background__glow--one" />
                <div className="landing-background__glow landing-background__glow--two" />
            </div>

            <header className="landing-header">
                <div className="landing-container landing-header__inner">
                    <a className="landing-brand" href="#top" aria-label="StratSync home">
                        <img className="landing-brand__mark" src="/images/Stratsync_Logo.webp" alt="" />
                        <img className="landing-wordmark-image" src="/images/stratsync-wordmark.svg" alt="" aria-hidden="true" />
                    </a>

                    <nav className="landing-nav" aria-label="Landing page navigation">
                        <a href="#features">Features</a>
                        <a href="#beyond-matchday">Beyond matchday</a>
                        <a href="#how-it-works">How it works</a>
                        <a href="#pricing">Pricing</a>
                    </nav>

                    <div className="landing-header__actions">
                        <button className="header-cta" type="button" onClick={handleFaceitLogin}>
                            Get started <ArrowRight size={15} />
                        </button>
                    </div>
                </div>
            </header>

            <main>
                <section className="landing-hero" id="top">
                    <video ref={heroVideoRef} muted playsInline className="landing-hero__video" />
                    <div className="landing-hero__shade" aria-hidden="true" />
                    <div className="landing-container landing-hero__inner">
                        <div className="landing-hero__copy">
                            <h1>Prepare smarter.<br /><span>Play in sync.</span></h1>
                            <p>
                                StratSync brings everything your team needs into one place — schedule availability, find scrim partners, build strategies, analyze demos and opponents, and launch dedicated practice and warm-up servers.
                            </p>
                            <div className="landing-hero__audience">
                                Built for competitive <strong>Counter-Strike 2 teams.</strong>
                            </div>
                            <div className="landing-hero__actions">
                                <button type="button" className="landing-primary-cta" onClick={handleFaceitLogin}>
                                    Start with FACEIT <ArrowRight size={17} />
                                </button>
                                <a className="landing-secondary-cta" href="#how-it-works">
                                    See how it works <ChevronRight size={17} />
                                </a>
                            </div>
                            <div className="landing-trust-row">
                                <span><Check size={14} /> Built for complete rosters</span>
                                <span><Check size={14} /> One connected workflow</span>
                            </div>
                        </div>

                        <HeroRankJourney />
                    </div>
                    <a className="hero-scroll-cue" href="#features"><span>Explore the platform</span><ChevronRight size={15} /></a>
                </section>

                <section className="features-section landing-section" id="features">
                    <div className="landing-container">
                        <div className="landing-section-heading landing-section-heading--left landing-reveal">
                            <span className="landing-section-kicker">Features</span>
                            <h2>Everything your team needs to get match-ready.</h2>
                            <p>Six connected workflows take your team from focused practice to match review, all in one place.</p>
                        </div>

                        <div className="feature-list">
                            {LANDING_FEATURES.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <article className={`feature-row feature-row--${feature.accent} ${index % 2 === 1 ? 'feature-row--reverse' : ''}`} key={feature.title}>
                                        <div className="feature-row__visual landing-reveal">
                                            {feature.videoSrc && feature.videoPoster ? <FeatureVideo
                                                src={feature.videoSrc}
                                                poster={feature.videoPoster}
                                                label={feature.eyebrow + ' product demonstration'}
                                            /> : <div className="feature-media feature-media--pending" role="img" aria-label={feature.eyebrow + ' video coming soon'}>
                                                <span className="feature-media__pending-icon" aria-hidden="true"><Play size={30} /></span>
                                                <strong>{feature.eyebrow}</strong>
                                                <span>Video coming soon</span>
                                            </div>}
                                        </div>
                                        <div className="feature-row__copy landing-reveal">
                                            <span className="feature-eyebrow"><Icon size={15} /> {feature.eyebrow}</span>
                                            <h3>{feature.title}</h3>
                                            <p>{feature.description}</p>
                                            <div className="feature-tags">
                                                {feature.highlights.map(highlight => <span key={highlight}><Check size={13} /> {highlight}</span>)}
                                            </div>
                                            {feature.detail && <section className="feature-detail" aria-label={feature.detail.title}>
                                                <h4>{feature.detail.title}</h4>
                                                <p>{feature.detail.description}</p>
                                            </section>}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className="supporting-section landing-section" id="beyond-matchday">
                    <div className="landing-container">
                        <div className="supporting-heading landing-reveal">
                            <div>
                                <span className="landing-section-kicker">Much more</span>
                                <h2>And so much more for your team.</h2>
                            </div>
                            <p>Keep your roster, calendar, season and communication connected — and turn your best moments into highlights.</p>
                        </div>
                        <div className="supporting-grid">
                            {SUPPORTING_FEATURES.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <article className="supporting-card landing-reveal" style={{ '--reveal-delay': `${(index % 3) * 70}ms` } as CSSProperties} key={feature.title}>
                                        <Icon size={21} />
                                        <h3>{feature.title}</h3>
                                        <p>{feature.description}</p>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className="workflow-section landing-section" id="how-it-works">
                    <div className="landing-container">
                        <div className="landing-section-heading landing-reveal">
                            <span className="landing-section-kicker">How it works</span>
                            <h2>Your complete competitive cycle in one place.</h2>
                            <p>From league import and match preparation to post-match review, StratSync connects every step that moves a CS2 team forward.</p>
                        </div>
                        <div className="workflow-phases">
                            {WORKFLOW_PHASES.map((phase, phaseIndex) => (
                                <section className="workflow-phase landing-reveal" key={phase.number}>
                                    <div className="workflow-phase__header">
                                        <span className="workflow-phase__number">{phase.number}</span>
                                        <div>
                                            <span className="workflow-phase__kicker">{phase.kicker}</span>
                                            <h3>{phase.title}</h3>
                                            <p>{phase.description}</p>
                                        </div>
                                    </div>
                                    <div className="workflow-phase__steps">
                                        {phase.steps.map((step, stepIndex) => {
                                            const Icon = step.icon;
                                            return (
                                                <article
                                                    className="workflow-step landing-reveal"
                                                    style={{ '--reveal-delay': `${(phaseIndex * 3 + stepIndex) * 35}ms` } as CSSProperties}
                                                    key={step.number}
                                                >
                                                    <span className="workflow-step__number">{step.number}</span>
                                                    <div className="workflow-step__icon"><Icon size={20} /></div>
                                                    <h4>{step.title}</h4>
                                                    <p>{step.description}</p>
                                                </article>
                                            );
                                        })}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="pricing-section landing-section" id="pricing">
                    <div className="landing-container">
                        <div className="landing-section-heading landing-reveal">
                            <span className="landing-section-kicker">Pricing</span>
                            <h2>Start where you are. Scale with the team.</h2>
                            <p>Premium is currently available by request during Beta. These are the planned monthly prices.</p>
                        </div>
                        <div className="pricing-grid">
                            {PRICING_PLANS.map((plan, index) => (
                                <article className={`pricing-card landing-reveal ${plan.featured ? 'pricing-card--featured' : ''}`} style={{ '--reveal-delay': `${index * 80}ms` } as CSSProperties} key={plan.name}>
                                    {plan.featured && <span className="pricing-popular">Most popular</span>}
                                    <div className="pricing-card__header">
                                        <span>{plan.audience}</span>
                                        <h3>{plan.name}</h3>
                                    </div>
                                    <div className="pricing-price"><sup>€</sup><strong>{plan.price}</strong><span>/ month</span></div>
                                    {plan.delivery && <div className={`pricing-delivery ${plan.featured ? 'pricing-delivery--demand' : 'pricing-delivery--dedicated'}`}>
                                        <strong>12-slot Scrim Server</strong>
                                        <span className="pricing-delivery__mode">{plan.delivery}</span>
                                        <p>{plan.deliveryNote}</p>
                                    </div>}
                                    <ul>
                                        {plan.features.map(feature => <li key={feature}><Check size={14} /> {feature}</li>)}
                                    </ul>
                                </article>
                            ))}
                        </div>
                        <p className="pricing-note landing-reveal">Player and Team Premium are separate entitlements and can be active at the same time.</p>
                    </div>
                </section>

                <section className="final-cta-section">
                    <div className="landing-container">
                        <div className="final-cta landing-reveal">
                            <div className="final-cta__glow" aria-hidden="true" />
                            <div>
                                <span className="landing-section-kicker">Ready for the next match?</span>
                                <h2>Bring the whole team into sync.</h2>
                                <p>Analyze, prepare and practice with a workflow designed for competitive CS2.</p>
                            </div>
                            <button type="button" onClick={handleFaceitLogin}>Start with FACEIT <ArrowRight size={17} /></button>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="footer-landing">
                <div className="landing-container footer-landing__inner">
                    <div className="footer-brand">
                        <img src="/images/Stratsync_Logo.webp" alt="" />
                        <div><strong>STRATSYNC</strong><span>Competitive CS2 team management</span></div>
                    </div>
                    <div className="footer-links-landing">
                        <a href="#features">Features</a>
                        <a href="#beyond-matchday">Beyond matchday</a>
                        <a href="#how-it-works">How it works</a>
                        <a href="#pricing">Pricing</a>
                        <a href="/documentation">Documentation</a>
                        <Link to="/imprint">Imprint</Link>
                        <Link to="/privacy">Privacy</Link>
                        <Link to="/terms">Terms</Link>
                    </div>
                    <div className="footer-copyright">© {new Date().getFullYear()} StratSync.</div>
                </div>
            </footer>
        </div>
    );
}
